import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  IconTooltip,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  cn,
} from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { PartnerService, UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { LinkIcon, ClipboardDocumentIcon, CheckIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { SendAssignment } from './SendAssignment'
import { DeleteDialog } from '../../DeleteDialog'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { AssignmentProgressTable } from './AssignmentProgressTable'
import { AssignmentActionsDropdown } from './AssignmentActionsDropdown'
import { useAssignmentActions } from './useAssignmentActions'
import { CohortPersonDetails } from '../cohortPageData'
import { toast } from 'sonner'
import { DevPrint } from '@components/DevPrint'

interface PublicAssignmentEntryProps {
  assignment: PartnerService.Assignment
  peopleDetails: CohortPersonDetails[]
  persons: UserService.Person[]
  getDisplayName: (taskId: string) => string
  isTaskLoading: boolean
  onAssignmentChange?: () => void
}

export const PublicAssignmentEntry: React.FC<PublicAssignmentEntryProps> = ({
  assignment,
  peopleDetails,
  persons,
  getDisplayName,
  isTaskLoading,
  onAssignmentChange,
}) => {
  const { t } = useTar()
  const [copied, setCopied] = useState(false)
  const {
    handleEdit,
    handleDelete,
    handleSetStatus,
    isActive,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
  } = useAssignmentActions({ assignment, onAssignmentChange })

  const isDueDatePast = assignment.dueDate
    ? new Date(assignment.dueDate) < new Date()
    : false
  const isEffectivelyInactive = !isActive || isDueDatePast

  const publicLink = assignment.linkToken
    ? `${window.location.origin}/?atoken=${assignment.linkToken}`
    : ''

  const inactiveCardClassName = cn(
    isEffectivelyInactive && 'opacity-85 bg-muted/25 border-muted-foreground/50'
  )

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink)
      setCopied(true)
      toast.success(t('common.linkCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('common.failedToCopyLink'))
    }
  }

  return (
    <Card className={inactiveCardClassName}>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="flex-1 text-xl font-semibold">
            {!isActive && (
              <span className="inline-flex items-center rounded-md border border-muted-foreground/50 bg-muted-foreground/20 px-2.5 py-1 text-xs font-semibold text-muted-foreground me-2">
                {t('common.inactive')}
              </span>
            )}
            {isTaskLoading ? (
              <span className="text-lg font-semibold">{t('common.loading')}</span>
            ) : (
              getDisplayName(assignment.taskId)
            )}

            <DevPrint label="assignmentId" value={assignment.id} />
          </CardTitle>
          <div className="flex items-center gap-2">
            <AssignmentActionsDropdown
              isActive={!isEffectivelyInactive}
              onEdit={handleEdit}
              onDeleteClick={() => setIsDeleteDialogOpen(true)}
              onSetStatus={handleSetStatus}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-6">
        <div className="space-y-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row">
              <div className="flex flex-col gap-2 font-bold text-foreground">{t('partner.timeWindow')}:</div>
              <div className="text-end flex-1">
                <div className="flex-row flex items-center justify-end gap-2">
                  {isEffectivelyInactive ? (
                    <div
                      className={cn(
                        'flex items-center text-sm gap-1',
                        'opacity-60'
                      )}
                    >
                      <LinkIcon className="h-4 w-4 text-primary" />
                      <span className="font-medium text-primary">{t('partner.publicLink')}</span>
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={publicLink ? handleCopyLink : undefined}
                          onKeyDown={(e) => {
                            if (publicLink && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault()
                              handleCopyLink()
                            }
                          }}
                          className={cn(
                            'flex items-center text-sm gap-1',
                            publicLink && 'cursor-pointer hover:opacity-80'
                          )}
                        >
                          <LinkIcon className="h-4 w-4 text-primary" />
                          <span className="font-medium text-primary">{t('partner.publicLink')}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{t('common.clickToCopy')}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row flex-wrap gap-2 lg:gap-4 text-sm">
              {assignment.startDate && (
                <div>
                  <span className="font-medium">{t('partner.startDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.startDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.dueDate && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t('partner.dueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.dueDate}
                    className="text-muted-foreground"
                  />
                  {isDueDatePast && (
                    <span className="inline-flex items-center rounded-md border border-muted-foreground/50 bg-muted-foreground/20 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                      {t('challenges.finished')}
                    </span>
                  )}
                </div>
              )}
              {assignment.overdueDate && (
                <div>
                  <span className="font-medium">{t('partner.overdueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.overdueDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.timeLimit != null && assignment.timeLimit > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t('partner.timeLimit')}:</span>{' '}
                  <span className="text-muted-foreground">
                    {(() => {
                      const totalMinutes = assignment.timeLimit ?? 0
                      const isWholeHours = totalMinutes % 60 === 0
                      const amount = isWholeHours ? totalMinutes / 60 : totalMinutes
                      const isSingular = amount === 1
                      const unitLabel = isWholeHours
                        ? (isSingular ? t('common.hour') : t('common.hours'))
                        : (isSingular ? t('common.minute') : t('common.minutes'))
                      return `${amount} ${unitLabel}`
                    })()}
                  </span>
                </div>
              )}
              {!assignment.startDate && !assignment.dueDate && !assignment.overdueDate && !assignment.timeLimit && (
                <div className="text-muted-foreground">{t('partner.noRestriction')}</div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              {t('partner.shareableLink')}
              <IconTooltip content={t('partner.publicAssignmentDescriptionTooltip')} />
            </div>
            <div className="flex gap-2">
              <Input
                value={publicLink}
                readOnly
                className={cn(
                  'flex-1 font-mono text-sm',
                  isEffectivelyInactive && 'opacity-60 cursor-not-allowed'
                )}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                disabled={isEffectivelyInactive}
              >
                {copied ? (
                  <CheckIcon className="h-4 w-4 text-green-500" />
                ) : (
                  <ClipboardDocumentIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          {peopleDetails.length === 0 ? (
            <NoDataEmptyState
              className="py-4"
              icon={UserGroupIcon}
              title={t('partner.noPeopleJoinedYet')}
              description={t('partner.noPeopleJoinedDescription')}
            />
          ) : (
            <AssignmentProgressTable
              peopleDetails={peopleDetails}
              persons={persons}
              sentTime={assignment.createdAt ?? ''}
              noCreatedColumn
              inactive={isEffectivelyInactive}
            />
          )}
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent width="sm">
          <DialogHeader>
            <DialogTitle>{t('common.editAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={assignment.cohortId}
            personIds={[]}
            assignmentId={assignment.id}
            publicMode
            onClose={() => setIsEditDialogOpen(false)}
            onSend={() => {
              setIsEditDialogOpen(false)
              onAssignmentChange?.()
            }}
          />
        </DialogContent>
      </Dialog>

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title={t('common.deleteAssignment')}
        description={t('common.assignmentDeletedDescription')}
      />
    </Card>
  )
}
